/**
 * AppConfig Feature Flags CrossFrame Listener
 *
 * Exposes the AppConfig-backed feature flags to child tool iframes by delegating to the
 * shell's existing `featureFlagService` -- the SAME framework `useFeatureFlag` uses. This
 * deliberately does NOT call the Gateway `getFeatureFlags` client directly: routing through
 * `featureFlagService` reuses the FEATURE_FLAG_REGISTRY validation, 20-minute cache,
 * in-flight dedup, stale-while-revalidate, fail-open defaults, and logout invalidation, and
 * keeps a single source of truth shared with the shell's own React consumers.
 *
 * This is distinct from `getFeatureFlagListener` (featureFlagListeners.ts), which serves the
 * separate IhmVirgo `GetFeatureFlagValue` API.
 *
 * Request  data: { flagNames: string[]; storeId?: string | null }
 *   - flagNames: required, non-empty array of REGISTERED flag names.
 *   - storeId:   optional. Required for store-scoped flags; ignored for global flags.
 *                Omitted / null / undefined => global (non-store-scoped) evaluation only.
 *
 * Response: { flags: { [flagName]: { enabled, attributes, source, error } } }
 *   - Always returns an entry for every requested flag (never partial), so a caller can rely
 *     on the shape. Unknown/unregistered flags fail open to `{ enabled: false }` with an
 *     `error` string rather than rejecting the whole batch.
 */

import { featureFlagService } from '@services/featureFlags'
import { FEATURE_FLAG_REGISTRY } from '@services/featureFlags/featureFlagRegistry'
import { validateRequiredArray } from './listenerUtils'

/** Per-flag result returned to the requesting iframe tool. */
export interface IAppConfigFlagResult {
    /** Effective flag value (registry default when the backend omitted it or on failure). */
    enabled: boolean
    /** Arbitrary key-value attributes for parameterized flags; {} when none. */
    attributes: Record<string, string>
    /** 'backend' when the value came from AppConfig, 'default' on fail-open / registry fallback. */
    source: 'backend' | 'default'
    /** Diagnostic message when the value is a fallback; null on a clean backend value. */
    error: string | null
}

export interface IGetAppConfigFeatureFlagsOutput {
    flags: Record<string, IAppConfigFlagResult>
}

export async function getAppConfigFeatureFlagsListener (
    message: any,
): Promise<IGetAppConfigFeatureFlagsOutput> {
    const params = (message && message.data) || {}

    // Edge case: missing / empty / non-array flagNames -> throws "flagNames is required".
    validateRequiredArray(params, 'flagNames')

    // validateRequiredArray only checks truthiness + `.length`, so a non-array with a length
    // (e.g. the bare string "profile_settings_v1") would slip through and then throw an
    // unhandled TypeError on `.filter` below. Guard explicitly so malformed input fails with a
    // clear, graceful error rather than a crash. (AutoSDE f-0bc1dbef.)
    if (!Array.isArray(params.flagNames)) {
        throw new Error('flagNames must be an array')
    }

    // Edge case: de-duplicate and drop non-string / blank names so a malformed entry from the
    // iframe cannot reach featureFlagService.get (which would throw on a non-registered value).
    const requestedNames: string[] = Array.from(
        new Set(
            (params.flagNames as unknown[])
                .filter((n): n is string => typeof n === 'string' && n.trim().length > 0)
                .map((n) => n.trim()),
        ),
    )

    // Normalize storeId: treat '', null, undefined uniformly as "no store" (global-only scope).
    const rawStoreId = params.storeId
    const storeId: string | null =
        typeof rawStoreId === 'string' && rawStoreId.trim().length > 0 ? rawStoreId.trim() : null

    if (requestedNames.length === 0) {
        // Every provided name was malformed/blank; nothing to resolve.
        return { flags: {} }
    }

    // Cache-first warmup: only fetch when a requested flag has no backing value yet.
    // featureFlagService.get() reports loading=true only for an EMPTY/FETCHING scope, so on a
    // warm cache (the FeatureFlagProvider prefetches at login and on store switch) this is a
    // no-op and NO extra Gateway call is made. prefetchGlobal/prefetchForStore are themselves
    // cache-first and in-flight-deduped, so concurrent tool requests coalesce.
    const needsWarmup = requestedNames.some((name) => {
        try {
            return featureFlagService.get(name, storeId).loading
        } catch {
            // Unregistered flag -- handled per-flag below; do not trigger a warmup for it.
            return false
        }
    })

    if (needsWarmup) {
        await Promise.all([
            // prefetchGlobal is now awaitable; it only fetches non-store-scoped flags.
            featureFlagService.prefetchGlobal(),
            // Only fetch the store scope when we actually have a store; prefetchForStore
            // no-ops on an empty id.
            storeId ? featureFlagService.prefetchForStore(storeId) : Promise.resolve(),
        ])
    }

    const flags: Record<string, IAppConfigFlagResult> = {}
    for (const name of requestedNames) {
        try {
            const state = featureFlagService.get(name, storeId)
            // Disambiguate misuse from a genuine value: a store-scoped flag read without a
            // storeId makes featureFlagService.get() early-return the registry default with
            // source:'default', error:null -- which is otherwise indistinguishable from a real
            // backend-disabled value. Surface an explicit error so the tool can detect it.
            // (AutoSDE f-24f12171.) The registry is imported directly (not via the mockable
            // service index) so this classification is always accurate.
            const registryEntry = FEATURE_FLAG_REGISTRY[name]
            const missingStoreScope = !!registryEntry?.isStoreScoped && storeId === null
            flags[name] = {
                enabled: state.enabled,
                attributes: state.attributes ?? {},
                source: state.source,
                error: missingStoreScope
                    ? `storeId is required to evaluate store-scoped flag "${name}"; returning registry default`
                    : state.error,
            }
        } catch (err: any) {
            // Edge case: flag not present in FEATURE_FLAG_REGISTRY -> get() throws. Fail open to
            // disabled and surface the reason instead of failing the entire batch, so one bad
            // name from a tool cannot deny it every other flag it asked for.
            flags[name] = {
                enabled: false,
                attributes: {},
                source: 'default',
                error: err?.message ?? String(err),
            }
        }
    }

    return { flags }
}
